import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../contexts/AppContext';
import { authService } from '../services/authService';
import { Zap, User, Settings, Globe, Menu, X, LogOut, RotateCcw, Table, HelpCircle, History } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { HelpModal } from './HelpModal';
import { useState, useEffect } from 'react';

export function Navigation() {
  const { state, logout } = useApp();
  const { t } = useTranslation();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Check if current user is a Kiosk user
  const isKiosk = authService.isKioskUser();

  // Handler for kiosk cleanup
  const handleKioskCleanup = () => {
    if (authService.ensureKioskMode()) {
      console.log('Kiosk mode: Manual cleanup completed');
      // Optional: Show a brief success indication
      alert('Kiosk mode reset completed');
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/', label: t('navigation.chargingQueue'), icon: Zap },
    { path: '/session', label: t('navigation.mySession'), icon: Zap },
    { path: '/charger-table', label: t('navigation.chargerTable'), icon: Table },
    { path: '/activity', label: t('navigation.activity'), icon: History },
    { path: '/profile', label: t('navigation.profile'), icon: User },
  ];

  // Add admin tab for admin users
  if (state.user?.role === 2) { // 2 = admin
    navItems.push({ path: '/admin', label: t('navigation.admin'), icon: Settings });
    // Add websocket test link for admins
    navItems.push({ 
      path: '/websocket-test.html', 
      label: 'WebSocket Test', 
      icon: Zap 
    });
  }

  // Close sidebar when route changes (except for admins)
  useEffect(() => {
    setIsSidebarOpen(false);
    // Only prevent opening for non-admin Kiosk users
    if (isKiosk && state.user?.role !== 2) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isKiosk, state.user?.role]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      // For full-screen sidebar, we don't need click outside detection
      // Users should use the close button or navigation
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  // Prevent body scroll when sidebar is open and handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only prevent keyboard shortcuts for non-admin Kiosk users
      if (isKiosk && state.user?.role !== 2) {
        // Block common menu shortcuts like Escape, F10, Alt+Space, etc.
        if (event.key === 'Escape' || event.key === 'F10' || 
            (event.altKey && event.key === ' ')) {
          event.preventDefault();
          return;
        }
      }
    };

    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSidebarOpen, isKiosk, state.user?.role]);

  const handleLogout = () => {
    logout();
    setIsSidebarOpen(false);
  };

  return (
    <>
      {/* Header without hamburger - clean header with just title and status */}
      <header className="modern-header">
        <div className="header-title">
          <h1>ChargeQueue</h1>
          <div className="connection-led" id="connection-led">
            <div className="led-dot disconnected"></div>
          </div>
          {isKiosk && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="kiosk-indicator" style={{ 
                fontSize: '0.85rem', 
                color: '#d97706', 
                marginLeft: '0.75rem',
                fontWeight: '600',
                backgroundColor: '#fef3c7',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #f59e0b'
              }}>
                🏪 Kiosk Mode
              </span>
              <button 
                onClick={handleKioskCleanup}
                className="kiosk-cleanup-btn"
                title="Reset Kiosk Mode"
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  padding: '0.25rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px'
                }}
              >
                <RotateCcw size={12} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Floating Action Button for Navigation - Always shown for admins, hidden for non-admin kiosk users */}
      {(!isKiosk || state.user?.role === 2) && (
        <button
          className="fab-menu-button"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Open navigation menu"
        >
          <Menu size={24} />
        </button>
      )}

      <style>{`
        /* Clean header without hamburger menu */
        .modern-header {
          justify-content: center !important;
        }
        
        .header-title {
          display: flex !important;
          align-items: center !important;
          gap: 0.5rem !important;
        }
        
        /* Floating Action Button */
        .fab-menu-button {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: var(--color-primary);
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1);
          z-index: 1001;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
        }
        
        .fab-menu-button:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 8px rgba(0, 0, 0, 0.15);
          background: var(--color-primary-hover);
        }
        
        .fab-menu-button:active {
          transform: scale(0.95);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        
        /* Add subtle animation when sidebar is open */
        .fab-menu-button:has(+ nav.modern-sidebar.open),
        .modern-sidebar.open ~ .fab-menu-button {
          background: var(--color-secondary);
          transform: rotate(90deg);
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .fab-menu-button {
            bottom: 24px;
            right: 24px;
            width: 60px;
            height: 60px;
          }
        }
        
        /* Safe area adjustments for phones with home indicators */
        @media (max-width: 480px) {
          .fab-menu-button {
            bottom: max(20px, env(safe-area-inset-bottom, 20px));
            right: max(20px, env(safe-area-inset-right, 20px));
          }
        }
        
        /* Ensure FAB is visible above other content */
        .fab-menu-button::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 50%;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .fab-menu-button:hover::before {
          opacity: 1;
        }
        
        .connection-led {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .led-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }
        
        .led-dot.connected {
          background-color: #4caf50;
          box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3), 0 0 6px rgba(76, 175, 80, 0.5);
        }
        
        .led-dot.disconnected {
          background-color: #f44336;
          box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.3), 0 0 6px rgba(244, 67, 54, 0.5);
        }
        
        .led-dot.connecting {
          background-color: #ff9800;
          box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.3), 0 0 6px rgba(255, 152, 0, 0.5);
          animation: pulse-led 1.5s infinite;
        }
        
        @keyframes pulse-led {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Sidebar Overlay - Not needed for full-screen */}
      {/* Modern Full-Screen Sidebar - Always shown for admins, hidden for non-admin kiosk users */}
      {(!isKiosk || state.user?.role === 2) && (
        <nav className={`modern-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-title">
              <h2>ChargeQueue</h2>
              <p>EV Charging Manager</p>
            </div>
            <button
              className="sidebar-close"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close navigation menu"
            >
              <X size={20} />
            </button>
          </div>

          <div className="sidebar-content">
            {/* User Info */}
            {state.user && (
              <div className="sidebar-user-info">
                <div className="user-avatar">
                  <User size={24} />
                </div>
                <div className="user-details">
                  <h3>{state.user.fullName}</h3>
                  <p>
                    <a 
                      href={`tel:${state.user.phoneE164?.value.replace(/[^\d+]/g, '')}`}
                      className="phone-link subtle"
                      title={`Call ${state.user.fullName}`}
                    >
                      {state.user.phoneE164?.value}
                    </a>
                  </p>
                  <span className="user-role">
                    {state.user.role === 2 ? t('common.admin') : t('common.user')}
                  </span>
                </div>
              </div>
            )}

            {/* Navigation Items */}
            <div className="sidebar-nav-items">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  state={{ fromMenu: true }}
                  className={`sidebar-nav-item ${isActive(path) ? 'active' : ''}`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <div className="nav-item-icon">
                    <Icon size={20} />
                  </div>
                  <span className="nav-item-label">{label}</span>
                </Link>
              ))}
            </div>

            {/* Language Selector */}
            <div className="sidebar-section">
              <button
                className={`sidebar-nav-item ${showLanguageSelector ? 'active' : ''}`}
                onClick={() => setShowLanguageSelector(!showLanguageSelector)}
              >
                <div className="nav-item-icon">
                  <Globe size={20} />
                </div>
                <span className="nav-item-label">{t('navigation.language')}</span>
              </button>
              
              {showLanguageSelector && (
                <div className="sidebar-language-selector">
                  <LanguageSelector />
                </div>
              )}
            </div>

            {/* Help Button */}
            <div className="sidebar-section">
              <button
                className="sidebar-nav-item"
                onClick={() => {
                  setShowHelpModal(true);
                  setIsSidebarOpen(false);
                }}
              >
                <div className="nav-item-icon">
                  <HelpCircle size={20} />
                </div>
                <span className="nav-item-label">{t('navigation.help', 'Help & Guide')}</span>
              </button>
            </div>

            {/* Logout Button */}
            <div className="sidebar-footer">
              <button
                className="sidebar-nav-item logout-item"
                onClick={handleLogout}
              >
                <div className="nav-item-icon">
                  <LogOut size={20} />
                </div>
                <span className="nav-item-label">{t('common.logout')}</span>
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* Help Modal */}
      <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </>
  );
}